import { Trans } from '@lingui/react';
import { DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { apiClient } from '../../../../common/http';
import { useDocGenAgRegistrationFormMutation } from '../../../../graphql/react-operations';
import { AuthorizationDocument } from '../../Authorization.graphql';
import { omit } from 'lodash-es';
import {
  GqlAgValidatorErrors,
  GqlAuthorizationGroup,
  GqlDocSystemTagKind,
  GqlDocumentSignatureMethod,
  GqlMeeting,
  GqlMeetingConcern,
} from '../../../../graphql/base-types.graphql';
import { DocumentSignatures } from '../../../../components/document-signatures/DocumentSignatures';
import { Divider } from '@startuptools/ui/joy/Divider';
import { useMeetingHasQesRegFormsSuspenseQuery } from '../../../../graphql/react-operations';

export const AgStepDocument = ({
  authorizationGroup,
  canUseProFeatures,
  validatorErrors,
}: {
  authorizationGroup: Pick<GqlAuthorizationGroup, 'id' | 'defaultFilename' | 'companyId'> & {
    meetingConcern?: (Pick<GqlMeetingConcern, 'id' | 'meetingId'> & { meeting: Pick<GqlMeeting, 'id'> }) | null;
    latestDocument?: DisplayDocument | null;
  };
  canUseProFeatures: boolean;
  validatorErrors: GqlAgValidatorErrors;
}) => {
  const { data: { meetingHasQesRegForms } = { meetingHasQesRegForms: false } } = useMeetingHasQesRegFormsSuspenseQuery({
    variables: {
      companyId: authorizationGroup.companyId,
      input: { id: authorizationGroup.meetingConcern?.meetingId ?? '', skipId: authorizationGroup.id },
    },
    skip: !authorizationGroup.meetingConcern?.meetingId,
    fetchPolicy: 'no-cache',
  });
  // eslint-disable-next-line sut/loading-mutation
  const [generateForm] = useDocGenAgRegistrationFormMutation({
    refetchQueries: [AuthorizationDocument],
    awaitRefetchQueries: true,
  });

  const companyCanUseProFeatures = canUseProFeatures;

  return (
    <Stack gap={1}>
      <Typography level="h4">
        <Trans id="Document" />
      </Typography>
      <Document
        document={authorizationGroup.latestDocument}
        documentSystemTagKind={GqlDocSystemTagKind.AuthorizationRegistrationForm}
        hasDigitalRegistrationForms={meetingHasQesRegForms}
        defaultFileName={authorizationGroup.defaultFilename}
        validate={async () => {
          const errors = Object.values(omit(validatorErrors, 'signatories'));
          return {
            isValid: !errors.some(e => e.hasError),
            errors,
          };
        }}
        preview={() => apiClient.previewAgRegistrationForm(authorizationGroup.companyId, authorizationGroup.id)}
        generate={async signatureMethod => {
          await generateForm({
            variables: {
              companyId: authorizationGroup.companyId,
              agId: authorizationGroup.id,
              signatureMethod: signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
            },
          });
        }}
        allowDigitalSignature={companyCanUseProFeatures}
        canUseProFeatures={companyCanUseProFeatures}
        signaturesSlot={
          authorizationGroup.latestDocument?.signatureMethod === GqlDocumentSignatureMethod.Digital &&
          companyCanUseProFeatures && (
            <>
              <Divider />
              <DocumentSignatures
                documentId={authorizationGroup.latestDocument.id}
                scriveStatus={authorizationGroup.latestDocument.scriveStatus}
                zignedAgreementStatus={authorizationGroup.latestDocument.zignedAgreementStatus}
                signAllowedAction={authorizationGroup.latestDocument.signAllowedAction}
                isQualifiedEsign
              />
            </>
          )
        }
      />
    </Stack>
  );
};
